import { catchError, map } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorService, Error } from '@app/services/error.service';
import { ErrorCodes } from '@app/components/error/error.code';
import { Level, Role } from '@data/models/role.model';
import { User } from '@data/models/user.model';
import * as Sentry from '@sentry/browser';

import { AUTH_SERVICE_USERS_API_ROOT } from '@data/services/authorization.service';
import { USER_SERVICE_API_ROOT } from '@modules/users/user-api-root';
import { lastValueFrom } from 'rxjs';

export interface UserSession extends User {
    authorizations: Role[];
    maxLevelByEntities: {};
    resetPasswordUrl: string;
}

@Injectable()
export class UserSessionService {

    private userSession: UserSession;

    private errors: Error[];

    constructor(
        private errorService: ErrorService,
        private router: Router,
        private httpClient: HttpClient,
    ) {
        this.errors = this.errorService.userSessionServiceErrors;
    }

    async initSession(): Promise<UserSession> {
        // Get
        const me = await lastValueFrom(this.httpClient
            .get<User>(`${USER_SERVICE_API_ROOT}/me`)
            .pipe(
                catchError(this.errorService.errorHandler<User>(this.errors, 'Récupération de paramètres de compte', null))
            ));

        if (!me) {
            // eslint-disable-next-line
            await this.router.navigate(['/error'], { queryParams: { code: ErrorCodes.fail_to_get_me } });
        }
        const roles = await lastValueFrom(this.httpClient
            .get<{ authorizations: Role[] }>(`${AUTH_SERVICE_USERS_API_ROOT}/me/authorizations`)
            .pipe(
                map(result => result.authorizations),
                catchError(this.errorService.errorHandler<Role[]>(this.errors, 'Récupération de mes droits', []))
            ));

        // Process
        const maxLevelByEntities = {};
        roles.forEach(role => {
            role.acl.forEach(acl => {
                if (
                    (!maxLevelByEntities[acl.entity]) || // No ACL yet
                    (acl.level === Level.EDIT && maxLevelByEntities[acl.entity] !== Level.ADMIN) || // Higher or equal level
                    (acl.level === Level.ADMIN) // Highest level
                ) {
                    maxLevelByEntities[acl.entity] = acl.level;
                }
            });
        });

        // Save
        this.userSession = {
            ...me,
            ...{ authorizations: roles },
            ...{ maxLevelByEntities: maxLevelByEntities },
            resetPasswordUrl: `https://sso.eu.clara.net/adfs/portal/updatepassword?userName=${me.email}`
        };

        Sentry.configureScope((scope) => {
            scope.setUser({ id: me.id });
        });

        // Session initialized
        return this.userSession;
    }

    getUserSession(): UserSession {
        return this.userSession;
    }

    checkRight(entity: string, level: Level = Level.EDIT): boolean {
        if (!this.userSession.maxLevelByEntities[entity]) {
            return false;
        }
        return (
            level === this.userSession.maxLevelByEntities[entity] ||
            level === Level.READ ||
            this.userSession.maxLevelByEntities[entity] === Level.ADMIN
        );
    }
}
